import { TableProps } from "antd";
import {
  CommonPageResponse,
  CommonResponse,
} from "./commonInterface.interface";
import { GetStorageUsageByAccountResult } from "./storageInterface.interface";
import {
  BILLING_CYCLE,
  DISCOUNT_TIER,
  EVENT_HISTORY_TYPE,
} from "./subscription.interface";

export enum BILLING_TYPE {
  USAGE = "USAGE",
  KEY = "KEY",
  UNKNOWN = "UNKNOWN",
}

export enum PLAN_TYPE {
  CHANNEL = "CHANNEL",
  STORAGE = "STORAGE",
}
export interface AutoEnrollRequestDto {
  accountId: string;
  autoEnroll: boolean;
  isNow: boolean;
}

export interface DefaultCycleRequest {
  cycle: BILLING_CYCLE;
}

export interface ChannelUsageRequestDto extends DefaultCycleRequest {
  date: string;
}

export interface StatCardRequestDto {
  cycle: BILLING_CYCLE;
  type?: PLAN_TYPE;
}

export interface UsageStorageList {
  accountNumber: string;
  name: string;
  accountAdditionalNumber: string;
  createdAt: string;
  planActiveDate: string;
  planCloudStorage: number;
  timezone: string;
  cloudStorage: GetStorageUsageByAccountResult;
  accountId: string;
}

export interface UsageChannelDto {
  dueDate: string;
  list: UsageGraphDto[];
}
export interface UsageGraphDto {
  date: string;
  count: number;
}

export interface UsageStorage {
  amount: number;
  used: number;
}

export interface EventHistoryDto {
  planEventType: EVENT_HISTORY_TYPE;
  accountId: string;
  recorderId: string;
  change: number;
  totalChannel: number;
  totalStorage: number;
  createdAt: string;
  amount?: number;
  description?: string;
}

export interface GetPlanEventHistoryResponse extends CommonPageResponse {
  result: EventHistoryDto[];
}

export interface PlanEuListDto {
  name: string;
  accountId: string;
  accountNumber: string;
  cameras: number;
  usedStorage: number;
  totalStorage: number;
  planCloudStorage: number;
  isSuspended: number;
  cascadedSuspension: number;
  createdAt: string;
  activatedAt: string;
  enableEdit: boolean;
}

export interface UsageStorageResult {
  usage: UsageStorage;
  storages: UsageStorageList[];
}

export interface GetPlanEuResponse extends CommonPageResponse {
  result: PlanEuListDto[];
}

export interface GetPlanUsageGraphResponse extends CommonResponse {
  result: UsageChannelDto[];
}

export interface GetPlanUsageStorageResponse extends CommonResponse {
  result: UsageStorageResult;
}

export interface ActiveEndUserDto {
  endUser: number;
  inActivate: number;
}

export interface ActiveChannelDto {
  channel: number;
  channelPrice: number;
  sumChannel: number;
}

export interface ActiveStorageDto {
  storage: number;
  storagePrice: number;
  sumStorage: number;
}

export interface EstimateCostDto {
  cost: number;
}

export interface YearlyCreditDto {
  amount: number;
  contract: number;
  period: number;
}

export interface UsedCreditDto {
  used: number;
  start: string;
  end: string;
}

export interface LeftCreditDto {
  remains: number;
  utilization: number;
}

export interface BurnRatetDto {
  rate: number;
}

export interface ExpireCreditDto {
  expireDate: string;
  rate: number;
  remainDate: number;
}

export interface StatCardResultDto {
  cycle: BILLING_CYCLE;
  type: string;
  activeUser: ActiveEndUserDto;
  activeChannels: ActiveChannelDto;
  activeStorage: ActiveStorageDto;
  estimateCost: EstimateCostDto;
  yearlyCredit: YearlyCreditDto;
  usedCredit: UsedCreditDto;
  leftCredit: LeftCreditDto;
  burnRate: BurnRatetDto;
  expireCredit: ExpireCreditDto;
  cycleDays: number;
}

export interface PostPlanStatCardResponse extends CommonResponse {
  result: StatCardResultDto;
}

interface AddPaymentMethodBase {
  userEmail: string;
  channels: number;
  storage: number;
  isUpgrade?: boolean;
}

export interface AddPaymentMethodMonthlyRequest extends AddPaymentMethodBase {
  cycle: BILLING_CYCLE.MONTHLY | BILLING_CYCLE.TEST_2DAY;
}

export interface AddPaymentMethodYearlyRequest extends AddPaymentMethodBase {
  cycle: BILLING_CYCLE.YEARLY | BILLING_CYCLE.TEST_YEARLY;
  channelPrice: number;
  storagePrice: number;
  termYears: number;
}

export type AddPaymentMethodRequest =
  | AddPaymentMethodMonthlyRequest
  | AddPaymentMethodYearlyRequest;

export interface AddPaymentCompleteRequest {
  requestId: string;
  sessionId: string;
}

export interface AddPaymentMethodResponse extends CommonResponse {
  result: string;
}

export enum TERM_DISCOUNT_YEAR {
  YEAR1 = "YEAR1",
  YEAR2 = "YEAR2",
  YEAR3 = "YEAR3",
}

export interface YearPaymentPrice {
  id: number;
  name: TERM_DISCOUNT_YEAR;
  discount: number;
}

export interface PaymentPrice {
  tierName: DISCOUNT_TIER;
  tierDiscount: number;
  channelPrice: number;
  storagePrice: number;
  registerCamera: number;
  year: YearPaymentPrice[];
}

export type GetPlanServicesResponse = PaymentPrice;

export type AddPaymentCompleteResult = AddPaymentMethodRequest & {
  get_id: string;
  accountId: string;
  createdAt: string;
};

export interface AddPaymentCompleteResponse extends CommonResponse {
  result: AddPaymentCompleteResult;
}

export interface DowngradeResponse extends CommonResponse {
  result: DefaultCycleRequest;
}

export interface PlanDetailSummaryDto {
  channel: number;
  cloudStorage: {
    totalStorage: number;
    usedStorage: number;
  };
  cpOwnerName: string;
  cpOwnerEmail: string;
  isActive: boolean;
}

export interface GetPlanDetailSummaryResponse extends CommonResponse {
  result: PlanDetailSummaryDto[];
}

export interface UsageBreakDownDto {
  key: string;
  qty: number;
  unitPrice: number;
  amount: number;
  euCount: number;
}

export interface ChannelHistoryDto {
  get_id: string;
  planEventType: EVENT_HISTORY_TYPE;
  accountId: string;
  accountNumber: string;
  change: number;
  totalChannel: number;
  totalStorage: number;
  usageDate: string;
  createdAt: string;
}

export interface ChannelInvoiceDto {
  orgName: string;
  average: number;
  activated: number;
  amount: number;
  history: ChannelHistoryDto[];
}

export interface StorageInvoiceDto {
  orgName: string;
  purchased: number;
  since: string;
  proRated: number;
  amount: number;
}

export interface PlanInterfaceDto {
  customer: string;
  billingDate: string;
  usageBreakDown: UsageBreakDownDto[];
  channels: ChannelInvoiceDto[];
  storage: StorageInvoiceDto[];
  cycleEnd: string;
  cycleStart: string;
}

export interface GetPlanInvoice extends CommonResponse {
  result: PlanInterfaceDto;
}

export type BillToDateInvoiceMeta = Pick<
  PlanInterfaceDto,
  "customer" | "billingDate" | "cycleStart" | "cycleEnd"
>;

export interface BillToDatePresenterProps {
  invoiceMeta: BillToDateInvoiceMeta;
  subscriptionTableProps: TableProps<UsageBreakDownDto>;
  usageTableProps: TableProps<ChannelInvoiceDto>;
  storageTableProps: TableProps<StorageInvoiceDto>;
  isAllExpanded: boolean;
  onToggleExpandAll: () => void;
  isAbleExpand: boolean;
}
